import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import useModuleAccess from '../hooks/useModuleAccess'

const API_URL = process.env.REACT_APP_API_URL

const CategoryStatCardSkeleton = () => (
  <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
)

const CATEGORY_SORT_FIELDS = [
  'categoryId',
  'categoryName',
  'createdAt'
]

const CATEGORY_SORT_COLUMN_MAP = Object.fromEntries(
  CATEGORY_SORT_FIELDS.map((field) => [field, field])
)

const DEFAULT_SORT_BY = 'createdAt'
const DEFAULT_SORT_ORDER = 'desc'

function normalizeSortBy(value) {
  const field = String(value || '').trim()
  return CATEGORY_SORT_FIELDS.includes(field) ? field : DEFAULT_SORT_BY
}

function normalizeSortOrder(value) {
  const order = String(value || '').trim().toLowerCase()
  return order === 'asc' ? 'asc' : 'desc'
}

function parseApiResponse(raw) {
  let data = raw
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return raw
    }
  }
  if (data?.body != null) {
    if (typeof data.body === 'string') {
      try {
        return JSON.parse(data.body)
      } catch {
        return data.body
      }
    }
    return data.body
  }
  return data
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeApiPayload(raw) {
  let obj = raw
  if (!obj || typeof obj !== 'object') return raw
  obj = { ...obj }
  if (typeof obj.data === 'string') {
    obj.data = parseJsonIfString(obj.data)
  }
  if (
    obj.data &&
    typeof obj.data === 'object' &&
    typeof obj.data.data === 'string'
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) }
  }
  return obj
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const success = responseData.success
  return success === true || success === 'true' || success === 1 || success === '1'
}

function extractCategoriesPayload(responseData) {
  const root = responseData
  let dataBlock = root?.data != null ? root.data : root

  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') {
    return { categories: [], pagination: {}, sortBy: null, sortOrder: null }
  }

  if (Array.isArray(dataBlock)) {
    return {
      categories: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === 'object'
          ? root.pagination
          : {},
      sortBy: root.sortBy || null,
      sortOrder: root.sortOrder || null
    }
  }

  let list =
    dataBlock.categories ||
    dataBlock.categoryList ||
    dataBlock.items ||
    root.categories

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === 'string'
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data
    if (inner && typeof inner === 'object') {
      list = inner.categories || inner.categoryList || inner.items
    }
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {}

  if (typeof list === 'string') {
    list = parseJsonIfString(list)
  }

  const total =
    typeof pagination.total === 'number'
      ? pagination.total
      : typeof pagination.totalCount === 'number'
        ? pagination.totalCount
        : typeof dataBlock.totalCount === 'number'
          ? dataBlock.totalCount
          : typeof dataBlock.total === 'number'
            ? dataBlock.total
            : undefined

  return {
    categories: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === 'object' && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {},
    sortBy: dataBlock.sortBy || root.sortBy || null,
    sortOrder: dataBlock.sortOrder || root.sortOrder || null
  }
}

function extractCategoryCountsPayload(responseData) {
  const root = responseData || {}
  let dataBlock = root.data != null ? root.data : root

  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }

  if (
    dataBlock &&
    typeof dataBlock === 'object' &&
    dataBlock.data != null
  ) {
    dataBlock =
      typeof dataBlock.data === 'string'
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data
  }

  const counts =
    dataBlock && typeof dataBlock === 'object' && !Array.isArray(dataBlock)
      ? dataBlock
      : {}

  const toCount = (value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  return {
    categoryCount: toCount(counts.categoryCount),
    subcategoryCount: toCount(counts.subcategoryCount),
    totalActiveCount: toCount(counts.totalActiveCount)
  }
}

function categoryUserName(user) {
  if (!user) return '-'
  if (typeof user === 'string') return user.trim() || '-'
  return user.name || user.userName || '-'
}

function formatCategoryDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  })
}

function extractCategoryDetail(responseData) {
  const root = responseData
  if (!root || typeof root !== 'object') return null
  const data = root.data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data
  }
  return null
}

const CategoryDetailCard = ({ label, value, icon }) => (
  <div
    className="h-100"
    style={{
      backgroundColor: '#f8fafb',
      border: '1px solid #e3e8ee',
      borderRadius: '8px',
      padding: '14px 16px'
    }}
  >
    <div
      className="d-flex align-items-center mb-2"
      style={{ color: '#6c757d', fontSize: '12px', fontWeight: 600 }}
    >
      {icon ? <i className={`${icon} mr-2`} style={{ color: '#1199B2' }} /> : null}
      {label}
    </div>
    <div style={{ color: '#212529', fontSize: '14px', fontWeight: 600, wordBreak: 'break-word' }}>
      {value || '-'}
    </div>
  </div>
)

function categoryLabel(category) {
  const categoryName = String(category?.categoryName || '').trim()
  const subcategoryName = String(category?.subcategoryName || '').trim()
  if (categoryName && subcategoryName) return `${categoryName} - ${subcategoryName}`
  return categoryName || subcategoryName || '-'
}

function renderCategoryStatus(status) {
  const value = String(status || '').trim()
  if (!value) return '-'
  const normalized = value.toLowerCase()
  const badgeClass =
    normalized === 'active'
      ? 'badge-success'
      : normalized === 'inactive'
        ? 'badge-danger'
        : 'badge-secondary'
  return (
    <span className={`badge ${badgeClass}`} style={{ fontSize: '90%', fontWeight: 550 }}>
      {value}
    </span>
  )
}

const Category = () => {
  const { canCreate, canView, canUpdate, canDelete } = useModuleAccess(
    'category_management'
  )
  const savedFilters = JSON.parse(sessionStorage.getItem('categoryFilters') || '{}')

  const [categories, setCategories] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery || '')
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || ''
  )
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1)
  const [itemsPerPage, setItemsPerPage] = useState(savedFilters.itemsPerPage || 25)
  const [deleteCategoryId, setDeleteCategoryId] = useState(null)
  const [deleteCategoryLabel, setDeleteCategoryLabel] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [sortBy, setSortBy] = useState(
    normalizeSortBy(savedFilters.sortBy || DEFAULT_SORT_BY)
  )
  const [sortOrder, setSortOrder] = useState(
    normalizeSortOrder(savedFilters.sortOrder || DEFAULT_SORT_ORDER)
  )
  const [isCategoryCountLoading, setIsCategoryCountLoading] = useState(true)
  const [categoryCount, setCategoryCount] = useState({
    totalActiveCount: 0
  })
  const [nextCategoryId, setNextCategoryId] = useState('')
  const [addCategoryName, setAddCategoryName] = useState('')
  const [isLoadingNextCategoryId, setIsLoadingNextCategoryId] = useState(false)
  const [isCreatingCategory, setIsCreatingCategory] = useState(false)
  const [viewCategory, setViewCategory] = useState(null)
  const [isLoadingViewCategory, setIsLoadingViewCategory] = useState(false)
  const [editCategory, setEditCategory] = useState(null)
  const [editCategoryName, setEditCategoryName] = useState('')
  const [isLoadingEditCategory, setIsLoadingEditCategory] = useState(false)
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false)

  const fetchNextCategoryId = async () => {
    setIsLoadingNextCategoryId(true)
    setNextCategoryId('')

    try {
      const response = await axios.post(API_URL, {
        action: 'get-next-category-id'
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData) && responseData.data?.nextCategoryId) {
        setNextCategoryId(String(responseData.data.nextCategoryId))
      } else {
        toast.error(responseData?.message || 'Failed to fetch category ID', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Fetch next category ID error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while fetching category ID',
        { autoClose: 2000 }
      )
    } finally {
      setIsLoadingNextCategoryId(false)
    }
  }

  const handleOpenAddCategoryModal = () => {
    setAddCategoryName('')
    setNextCategoryId('')
    if (window.$) {
      window.$('#modaladdcategory').modal('show')
    }
    fetchNextCategoryId()
  }

  const handleCloseAddCategoryModal = () => {
    if (window.$) {
      window.$('#modaladdcategory').modal('hide')
    }
    setAddCategoryName('')
    setNextCategoryId('')
  }

  const handleCreateCategory = async () => {
    if (!addCategoryName.trim()) {
      toast.error('Category name is required', { autoClose: 2000 })
      return
    }

    if (!nextCategoryId) {
      toast.error('Category ID is not available. Please try again.', {
        autoClose: 2000
      })
      return
    }

    const createdBy = localStorage.getItem('id')
    if (!createdBy) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setIsCreatingCategory(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'create-category',
        categoryId: nextCategoryId,
        categoryName: addCategoryName.trim().toUpperCase(),
        createdBy
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(responseData?.message || 'Category created successfully', {
          autoClose: 2000
        })
        handleCloseAddCategoryModal()
        if (currentPage !== 1) {
          setCurrentPage(1)
        } else {
          fetchCategories()
        }
        fetchCategoryCounts()
      } else {
        toast.error(responseData?.message || 'Failed to create category', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Create category error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while creating category',
        { autoClose: 2000 }
      )
    } finally {
      setIsCreatingCategory(false)
    }
  }

  const handleCloseViewCategoryModal = () => {
    if (window.$) {
      window.$('#modalviewcategory').modal('hide')
    }
    setViewCategory(null)
  }

  const fetchCategoryById = async (categoryId) => {
    setIsLoadingViewCategory(true)
    setViewCategory(null)

    try {
      const response = await axios.post(API_URL, {
        action: 'get-category-by-id',
        id: categoryId
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      const category = extractCategoryDetail(responseData)

      if (isApiSuccess(responseData) && category) {
        setViewCategory(category)
      } else {
        toast.error(responseData?.message || 'Failed to fetch category details', {
          autoClose: 2000
        })
        handleCloseViewCategoryModal()
      }
    } catch (error) {
      console.error('Fetch category error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while fetching category details',
        { autoClose: 2000 }
      )
      handleCloseViewCategoryModal()
    } finally {
      setIsLoadingViewCategory(false)
    }
  }

  const handleViewClick = (category) => {
    const categoryId = category?._id || category?.id
    if (!categoryId) {
      toast.error('Category ID is missing', { autoClose: 2000 })
      return
    }

    if (window.$) {
      window.$('#modalviewcategory').modal('show')
    }
    fetchCategoryById(categoryId)
  }

  const handleCloseEditCategoryModal = () => {
    if (window.$) {
      window.$('#modaleditcategory').modal('hide')
    }
    setEditCategory(null)
    setEditCategoryName('')
  }

  const fetchCategoryForEdit = async (categoryId) => {
    setIsLoadingEditCategory(true)
    setEditCategory(null)
    setEditCategoryName('')

    try {
      const response = await axios.post(API_URL, {
        action: 'get-category-by-id',
        id: categoryId
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      const category = extractCategoryDetail(responseData)

      if (isApiSuccess(responseData) && category) {
        setEditCategory(category)
        setEditCategoryName(String(category.categoryName || '').toUpperCase())
      } else {
        toast.error(responseData?.message || 'Failed to fetch category details', {
          autoClose: 2000
        })
        handleCloseEditCategoryModal()
      }
    } catch (error) {
      console.error('Fetch category for edit error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while fetching category details',
        { autoClose: 2000 }
      )
      handleCloseEditCategoryModal()
    } finally {
      setIsLoadingEditCategory(false)
    }
  }

  const handleEditClick = (category) => {
    const categoryId = category?._id || category?.id
    if (!categoryId) {
      toast.error('Category ID is missing', { autoClose: 2000 })
      return
    }

    if (window.$) {
      window.$('#modaleditcategory').modal('show')
    }
    fetchCategoryForEdit(categoryId)
  }

  const handleUpdateCategory = async () => {
    if (!editCategoryName.trim()) {
      toast.error('Category name is required', { autoClose: 2000 })
      return
    }

    const id = editCategory?._id || editCategory?.id
    if (!id) {
      toast.error('Category ID is missing', { autoClose: 2000 })
      return
    }

    const updatedBy = localStorage.getItem('id')
    if (!updatedBy) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setIsUpdatingCategory(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'update-category',
        id,
        updatedBy,
        categoryName: editCategoryName.trim().toUpperCase()
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(responseData?.message || 'Category updated successfully', {
          autoClose: 2000
        })
        handleCloseEditCategoryModal()
        fetchCategories()
        fetchCategoryCounts()
      } else {
        toast.error(responseData?.message || 'Failed to update category', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Update category error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while updating category',
        { autoClose: 2000 }
      )
    } finally {
      setIsUpdatingCategory(false)
    }
  }

  const fetchCategoryCounts = useCallback(async () => {
    setIsCategoryCountLoading(true)

    try {
      const response = await axios.post(API_URL, {
        action: 'category-counts'
      })

      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        setCategoryCount(extractCategoryCountsPayload(responseData))
      } else {
        setCategoryCount({
          totalActiveCount: 0
        })
      }
    } catch (error) {
      console.error('Category counts error:', error)
    } finally {
      setIsCategoryCountLoading(false)
    }
  }, [])

  const fetchCategories = useCallback(async () => {
    setIsLoading(true)
    try {
      const payload = {
        action: 'get-all-categories',
        page: currentPage,
        limit: itemsPerPage,
        sortBy: normalizeSortBy(sortBy),
        sortOrder: normalizeSortOrder(sortOrder)
      }

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : ''
      if (trimmedSearch) {
        payload.search = trimmedSearch
      }

      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      const {
        categories: list,
        pagination,
        sortBy: responseSortBy,
        sortOrder: responseSortOrder
      } = extractCategoriesPayload(responseData)

      const ok =
        isApiSuccess(responseData) || (Array.isArray(list) && list.length > 0)

      if (ok) {
        setCategories(list)
        const total =
          typeof pagination.total === 'number'
            ? pagination.total
            : typeof pagination.totalCount === 'number'
              ? pagination.totalCount
              : list.length
        setTotalCount(total)

        if (responseSortBy && CATEGORY_SORT_FIELDS.includes(responseSortBy)) {
          setSortBy(responseSortBy)
        }
        if (responseSortOrder === 'asc' || responseSortOrder === 'desc') {
          setSortOrder(responseSortOrder)
        }
      } else {
        toast.error(responseData?.message || 'Failed to fetch categories', {
          autoClose: 2000
        })
        setCategories([])
        setTotalCount(0)
      }
    } catch (error) {
      console.error('Fetch categories error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      const msg =
        typeof errData === 'object' && errData?.message
          ? errData.message
          : 'An error occurred while fetching categories'
      toast.error(msg, { autoClose: 2000 })
      setCategories([])
      setTotalCount(0)
    } finally {
      setIsLoading(false)
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    sortBy,
    sortOrder
  ])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  useEffect(() => {
    fetchCategoryCounts()
  }, [fetchCategoryCounts])

  useEffect(() => {
    sessionStorage.setItem(
      'categoryFilters',
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        currentPage,
        itemsPerPage,
        sortBy,
        sortOrder
      })
    )
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder
  ])

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1)
  const startItem = categories.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0
  const endItem = Math.min(currentPage * itemsPerPage, totalCount)
  const hasActiveFilters = Boolean(activeSearchQuery)

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10))
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    }
  }

  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 7
    const tp = totalPages

    if (tp <= maxVisiblePages) {
      for (let i = 1; i <= tp; i++) pages.push(i)
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i)
      pages.push('ellipsis')
      pages.push(tp)
    } else if (currentPage >= tp - 3) {
      pages.push(1)
      pages.push('ellipsis')
      for (let i = tp - 4; i <= tp; i++) pages.push(i)
    } else {
      pages.push(1)
      pages.push('ellipsis')
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i)
      pages.push('ellipsis')
      pages.push(tp)
    }
    return pages
  }

  const handleClearAll = () => {
    setSearchQuery('')
    setActiveSearchQuery('')
    if (currentPage !== 1) setCurrentPage(1)
  }

  const handleSearchChange = (e) => {
    const newValue = e.target.value
    setSearchQuery(newValue)
    const trimmedValue = newValue ? newValue.trim() : ''
    if (trimmedValue === '' && activeSearchQuery !== '') {
      setActiveSearchQuery('')
      if (currentPage !== 1) setCurrentPage(1)
    }
  }

  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : ''
    if (trimmedQuery === '') {
      setActiveSearchQuery('')
      if (currentPage !== 1) setCurrentPage(1)
    } else {
      setActiveSearchQuery(trimmedQuery)
      if (currentPage !== 1) setCurrentPage(1)
    }
  }

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearchTrigger()
    }
  }

  const handleSort = (column) => {
    const apiField = CATEGORY_SORT_COLUMN_MAP[column]
    if (!apiField || !CATEGORY_SORT_FIELDS.includes(apiField)) return

    if (sortBy === apiField) {
      if (sortOrder === 'asc') {
        setSortOrder('desc')
      } else {
        setSortBy(DEFAULT_SORT_BY)
        setSortOrder(DEFAULT_SORT_ORDER)
      }
    } else {
      setSortBy(apiField)
      setSortOrder('asc')
    }

    if (currentPage !== 1) {
      setCurrentPage(1)
    }
  }

  const getSortIcon = (column) => {
    const apiField = CATEGORY_SORT_COLUMN_MAP[column]
    if (!apiField) return null

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: '0.8em', flexShrink: 0 }}
        />
      )
    }

    if (sortOrder === 'asc') {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: '0.8em', flexShrink: 0 }}
        />
      )
    }

    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: '0.8em', flexShrink: 0 }}
      />
    )
  }

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      onClick={() => handleSort(column)}
      style={{
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        ...extraStyle
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  )

  const handleDeleteClick = (category) => {
    const categoryId = category?._id || category?.id
    if (!categoryId) {
      toast.error('Category ID is missing', { autoClose: 2000 })
      return
    }
    setDeleteCategoryId(categoryId)
    setDeleteCategoryLabel(
      String(category?.categoryName || '').trim() || categoryLabel(category)
    )
  }

  const handleDelete = async () => {
    if (!deleteCategoryId) return

    const updatedBy = localStorage.getItem('id')
    if (!updatedBy) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setIsDeleting(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'delete-category',
        id: deleteCategoryId,
        updatedBy
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(responseData?.message || 'Category deleted successfully', {
          autoClose: 2000
        })
        if (window.$) {
          window.$('#modaldelete').modal('hide')
        }
        setDeleteCategoryId(null)
        setDeleteCategoryLabel('')
        if (categories.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1)
        } else {
          fetchCategories()
        }
        fetchCategoryCounts()
      } else {
        toast.error(responseData?.message || 'Failed to delete category', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Delete category error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(errData?.message || 'An error occurred while deleting category', {
        autoClose: 2000
      })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Category</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <button
                  type="button"
                  className="btn btn-blue"
                  onClick={handleOpenAddCategoryModal}
                >
                  Add Category
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row stat-card-row">
                {isCategoryCountLoading ? (
                  <CategoryStatCardSkeleton />
                ) : (
                  <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: '#196F3D',
                              fontSize: '13px',
                              fontWeight: '600',
                              marginBottom: '8px'
                            }}
                          >
                            Total Active Count
                          </div>
                          <div
                            className="stat-value"
                            style={{
                              color: '#27AE60',
                              fontSize: '22px',
                              fontWeight: '700'
                            }}
                          >
                            {categoryCount.totalActiveCount}
                          </div>
                        </div>
                      </div>
                    </div>
                )}
              </div>
              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-3">
                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: '13px', fontWeight: '500' }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search categories..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: 'pointer' }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : categories.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no categories yet.
                  <br />
                  {canCreate && (
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={handleOpenAddCategoryModal}
                      >
                        Add Category
                      </button>
                    </div>
                  )}
                </div>
              ) : categories.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No categories found matching your search.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="categories"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: 'nowrap',
                              width: '40px',
                              minWidth: '40px'
                            }}
                          >
                            S.No
                          </th>
                          {renderSortableHeader('categoryId', 'Category ID', {
                            minWidth: '110px'
                          })}
                          {renderSortableHeader('categoryName', 'Category Name', {
                            minWidth: '140px'
                          })}
                          <th style={{ minWidth: '90px' }}>Status</th>
                          <th style={{ minWidth: '140px' }}>Created By</th>
                          <th style={{ width: '80px', minWidth: '80px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((category, index) => {
                          const categoryId = category._id || category.id
                          return (
                            <tr key={categoryId || index}>
                              <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {category.categoryId || '-'}
                              </td>
                              <td
                                style={{
                                  maxWidth: '180px',
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal'
                                }}
                              >
                                {category.categoryName || '-'}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {renderCategoryStatus(category.status)}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {categoryUserName(category.createdBy)}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: 'nowrap' }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  {canView && (
                                    <a
                                      href="#modalviewcategory"
                                      className="crsr_pntr action-icon-link mr-2"
                                      title="View"
                                      onClick={() => handleViewClick(category)}
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </a>
                                  )}
                                  {canUpdate && (
                                    <a
                                      href="#modaleditcategory"
                                      className="crsr_pntr action-icon-link mr-2"
                                      title="Edit"
                                      onClick={() => handleEditClick(category)}
                                    >
                                      <i className="fa-solid fa-pen" />
                                    </a>
                                  )}
                                  {canDelete && (
                                    <a
                                      href="#modaldelete"
                                      className="crsr_pntr action-icon-link"
                                      data-toggle="modal"
                                      data-target="#modaldelete"
                                      title="Delete"
                                      onClick={() => handleDeleteClick(category)}
                                    >
                                      <i className="fa-solid fa-trash-can" />
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {!isLoading && categories.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: '13px' }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: '13px' }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: '13px' }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left" />
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left" />
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === 'ellipsis' ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? 'active' : ''
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              )
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? 'disabled' : ''
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right" />
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? 'disabled' : ''
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalviewcategory"
        tabIndex={-1}
        aria-labelledby="modalviewcategoryLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content" style={{ border: 'none', borderRadius: '10px', overflow: 'hidden' }}>
            <div
              className="modal-header border-0"
              style={{ backgroundColor: '#1199B2', color: '#fff', padding: '16px 20px' }}
            >
              <h5 className="modal-title mb-0" id="modalviewcategoryLabel">
                <i className="fa-solid fa-layer-group mr-2" />
                View Category
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseViewCategoryModal}
                style={{ color: '#fff', opacity: 1, textShadow: 'none' }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px', backgroundColor: '#fff' }}>
              {isLoadingViewCategory ? (
                <div className="text-center py-5">
                  <PulseLoader color="#1199B2" size={10} />
                  <p className="text-muted small mt-3 mb-0">Loading category details...</p>
                </div>
              ) : viewCategory ? (
                <>
                  <div
                    className="mb-4 p-3"
                    style={{
                      background: 'linear-gradient(135deg, #f0f9fb 0%, #e8f4f8 100%)',
                      border: '1px solid #cfe8ef',
                      borderRadius: '10px'
                    }}
                  >
                    <div className="d-flex align-items-start" style={{ gap: '16px' }}>
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#1199B2',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                            marginBottom: '6px'
                          }}
                        >
                          Category Name
                        </div>
                        <div
                          style={{
                            fontSize: '20px',
                            fontWeight: 700,
                            color: '#17324d',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere'
                          }}
                        >
                          {viewCategory.categoryName || '-'}
                        </div>
                      </div>
                      <div
                        className="d-flex flex-column align-items-end flex-shrink-0"
                        style={{ flex: '0 0 auto', marginLeft: 'auto' }}
                      >
                        <div
                          className="badge badge-light text-dark"
                          style={{ fontSize: '12px', padding: '6px 10px', whiteSpace: 'nowrap' }}
                        >
                          ID: {viewCategory.categoryId || '-'}
                        </div>
                        <div className="mt-2">{renderCategoryStatus(viewCategory.status)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-sm-6 mb-3">
                      <CategoryDetailCard
                        label="Created By"
                        value={categoryUserName(viewCategory.createdBy)}
                        icon="fa-solid fa-user"
                      />
                    </div>
                    <div className="col-sm-6 mb-3">
                      <CategoryDetailCard
                        label="Updated By"
                        value={categoryUserName(viewCategory.updatedBy)}
                        icon="fa-solid fa-user-pen"
                      />
                    </div>
                    <div className="col-sm-6 mb-0 mb-sm-0">
                      <CategoryDetailCard
                        label="Created At"
                        value={formatCategoryDate(viewCategory.createdAt)}
                        icon="fa-regular fa-calendar"
                      />
                    </div>
                    <div className="col-sm-6 mb-0">
                      <CategoryDetailCard
                        label="Updated At"
                        value={formatCategoryDate(viewCategory.updatedAt)}
                        icon="fa-regular fa-clock"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  <i className="fa-regular fa-folder-open fa-2x mb-3 d-block" />
                  Category details not available.
                </div>
              )}
            </div>
            <div
              className="modal-footer border-0 justify-content-center"
              style={{ backgroundColor: '#f8fafb', padding: '14px 20px' }}
            >
              <button
                type="button"
                className="btn btn-blue px-4"
                data-dismiss="modal"
                onClick={handleCloseViewCategoryModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaladdcategory"
        tabIndex={-1}
        aria-labelledby="modaladdcategoryLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content" style={{ border: 'none', borderRadius: '10px', overflow: 'hidden' }}>
            <div
              className="modal-header border-0"
              style={{ backgroundColor: '#1199B2', color: '#fff', padding: '16px 20px' }}
            >
              <h5 className="modal-title mb-0" id="modaladdcategoryLabel">
                <i className="fa-solid fa-circle-plus mr-2" />
                Add Category
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseAddCategoryModal}
                disabled={isCreatingCategory}
                style={{ color: '#fff', opacity: 1, textShadow: 'none' }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px', backgroundColor: '#fff' }}>
              <div
                className="mb-4 p-3"
                style={{
                  background: 'linear-gradient(135deg, #f0f9fb 0%, #e8f4f8 100%)',
                  border: '1px solid #cfe8ef',
                  borderRadius: '10px'
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#1199B2',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    marginBottom: '8px'
                  }}
                >
                  Category ID
                </div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#17324d' }}>
                  {isLoadingNextCategoryId ? (
                    <span className="text-muted" style={{ fontSize: '14px', fontWeight: 500 }}>
                      <PulseLoader color="#1199B2" size={6} className="mr-2" />
                      Loading...
                    </span>
                  ) : (
                    nextCategoryId || '-'
                  )}
                </div>
              </div>
              <div className="form-group mb-0">
                <label
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px'
                  }}
                >
                  Category Name
                </label>
                <textarea
                  rows={3}
                  className="form-control mt-2"
                  placeholder="Enter category name"
                  value={addCategoryName}
                  onChange={(e) => setAddCategoryName(e.target.value.toUpperCase())}
                  disabled={isCreatingCategory || isLoadingNextCategoryId}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #cfe8ef',
                    padding: '10px 14px',
                    fontSize: '15px',
                    resize: 'vertical',
                    minHeight: '78px',
                    textTransform: 'uppercase'
                  }}
                />
              </div>
            </div>
            <div
              className="modal-footer border-0 justify-content-center"
              style={{ backgroundColor: '#f8fafb', padding: '14px 20px' }}
            >
              <button
                type="button"
                className="btn btn-secondary mr-2 px-4"
                data-dismiss="modal"
                onClick={handleCloseAddCategoryModal}
                disabled={isCreatingCategory}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue px-4"
                onClick={handleCreateCategory}
                disabled={
                  isCreatingCategory ||
                  isLoadingNextCategoryId ||
                  !nextCategoryId
                }
              >
                {isCreatingCategory ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaleditcategory"
        tabIndex={-1}
        aria-labelledby="modaleditcategoryLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content" style={{ border: 'none', borderRadius: '10px', overflow: 'hidden' }}>
            <div
              className="modal-header border-0"
              style={{ backgroundColor: '#1199B2', color: '#fff', padding: '16px 20px' }}
            >
              <h5 className="modal-title mb-0" id="modaleditcategoryLabel">
                <i className="fa-solid fa-pen-to-square mr-2" />
                Edit Category
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseEditCategoryModal}
                disabled={isUpdatingCategory}
                style={{ color: '#fff', opacity: 1, textShadow: 'none' }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px', backgroundColor: '#fff' }}>
              {isLoadingEditCategory ? (
                <div className="text-center py-5">
                  <PulseLoader color="#1199B2" size={10} />
                  <p className="text-muted small mt-3 mb-0">Loading category details...</p>
                </div>
              ) : editCategory ? (
                <>
                  <div
                    className="mb-4 p-3"
                    style={{
                      background: 'linear-gradient(135deg, #f0f9fb 0%, #e8f4f8 100%)',
                      border: '1px solid #cfe8ef',
                      borderRadius: '10px'
                    }}
                  >
                    <div className="d-flex align-items-start" style={{ gap: '16px' }}>
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#1199B2',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                            marginBottom: '6px'
                          }}
                        >
                          Category ID
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#17324d' }}>
                          {editCategory.categoryId || '-'}
                        </div>
                      </div>
                      <div
                        className="d-flex flex-column align-items-end flex-shrink-0"
                        style={{ flex: '0 0 auto', marginLeft: 'auto' }}
                      >
                        {renderCategoryStatus(editCategory.status)}
                      </div>
                    </div>
                  </div>

                  <div className="form-group mb-0">
                    <label
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6c757d',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px'
                      }}
                    >
                      <i className="fa-solid fa-layer-group mr-2" style={{ color: '#1199B2' }} />
                      Category Name
                    </label>
                    <textarea
                      rows={3}
                      className="form-control mt-2"
                      placeholder="Enter category name"
                      value={editCategoryName}
                      onChange={(e) => setEditCategoryName(e.target.value.toUpperCase())}
                      disabled={isUpdatingCategory}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #cfe8ef',
                        padding: '10px 14px',
                        fontSize: '15px',
                        fontWeight: 600,
                        color: '#17324d',
                        resize: 'vertical',
                        minHeight: '78px',
                        textTransform: 'uppercase'
                      }}
                    />
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  <i className="fa-regular fa-folder-open fa-2x mb-3 d-block" />
                  Category details not available.
                </div>
              )}
            </div>
            <div
              className="modal-footer border-0 justify-content-center"
              style={{ backgroundColor: '#f8fafb', padding: '14px 20px' }}
            >
              <button
                type="button"
                className="btn btn-secondary mr-2 px-4"
                data-dismiss="modal"
                onClick={handleCloseEditCategoryModal}
                disabled={isUpdatingCategory}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue px-4"
                onClick={handleUpdateCategory}
                disabled={
                  isUpdatingCategory ||
                  isLoadingEditCategory ||
                  !editCategory ||
                  !editCategoryName.trim()
                }
              >
                {isUpdatingCategory ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaldelete"
        tabIndex={-1}
        aria-labelledby="modaldeleteLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteLabel">
                Delete Category
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                disabled={isDeleting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete
                <br />
                <span className="fwt_600">{deleteCategoryLabel || '-'}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Yes, delete'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Category
